import React, { useState, useMemo, useCallback } from 'react';
import { Stack, Grid, Typography } from '@mui/material';
import { formatDate } from '@/utils/constants';
import {
	useDeleteTaskMutation,
	useUpdateTaskMutation,
} from '@/features/engagements/engagementsApi';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { minimalInputTheme, minimalLabelTheme } from '@/app/theme';
import parse from 'html-react-parser';
import { useLocation } from 'react-router-dom';
import { useObjectContext } from '@/hooks/useObjectContext';
import EngagementsAccordion from '../cards/EngagementsAccordion';
import TaskCheckbox from './TaskCheckbox';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import GroupsIcon from '@mui/icons-material/Groups';
import ChecklistIcon from '@mui/icons-material/Checklist';
import BrokerAvatar from '../BrokerAvatar';
import DealInfo from '../cards/EngagementsAccordion/DealInfo';
import { openDialog } from '@/features/dialog/dialogSlice';
import { showToast } from '@/features/toast/toastSlice';
import { useDispatch } from 'react-redux';
import { safelyParseDate } from '@/utils/date';

export const TaskAccordion = ({ task }) => {
	const dispatch = useDispatch();

	const { dealRow, dealId, clientId, isClientView: isClient } = useObjectContext();

	const objectType = isClient ? 'client' : dealRow?.objectType;

	const location = useLocation();

	const openedTask = useMemo(() => {
		console.log('location', { location, task });

		if (location.state?.task) {
			return location.state.task == task.id;
		}
		return false;
	}, [location?.state?.task, task.id]);

	const [updateTask, { isLoading }] = useUpdateTaskMutation();
	const [deleteTask, { isLoading: isDeleteLoading }] = useDeleteTaskMutation();

	const { data: owner, ...ownerQuery } = useGetUserDetailsQuery(
		{ userId: task.ownerDocId },
		{ skip: !task.ownerDocId },
	);

	const ownerName = !ownerQuery?.isSuccess ? '' : (owner?.name ?? owner?.email ?? 'None');


	const dueDate = useMemo(() => {
		const dateObj = safelyParseDate(task.dueDate);
		return dateObj;
	}, [task.dueDate]);

	const lastUpdated = useMemo(() => safelyParseDate(task.lastUpdated), [task.lastUpdated]);

	const [dialogOpen, setDialogOpen] = useState(false);

	const handleOpenDialog = () => setDialogOpen(true);
	const handleCloseDialog = () => setDialogOpen(false);

	const handleConfirmDialog = async () => {
		handleCloseDialog();
		await archiveTask();
	};

	const completeTask = async () => {
		try {
			await updateTask({
				dealId,
				clientId,
				taskId: task.id,
				objectType,
				properties: {
					status: task.completed ? 'NOT_STARTED' : 'COMPLETED',
				},
			}).unwrap();
		} catch (err) {
			console.error('Error updating task', err);
			dispatch(
				showToast({
					message: `Error updating task: ${err?.error ?? 'Please try again'}`,
					error: true,
				}),
			);
		}
	};

	const archiveTask = async () => {
		await deleteTask({
			dealId,
			clientId,
			taskId: task.id,
			objectType,
		});
	};

	const TaskIcon = useCallback(
		({ ...params }) => {
			switch (task.taskType) {
				case 'CALL':
					return <PhoneIcon {...params} />;
				case 'EMAIL':
					return <EmailIcon {...params} />;
				case 'MEETING':
					return <GroupsIcon {...params} />;
				default:
					return <ChecklistIcon {...params} />;
			}
		},
		[task.taskType],
	);

	//Bundle up the client and deal with task data to send to task dialog
	// const engagement = {
	// 	...task,
	// 	client: client ?? null,
	// };

	// const associatedDeal = engagement?.data?.ticket ?? engagement?.data?.deal;

	const handleEditTask = () => {
		dispatch(openDialog({ dialogName: 'task', data: { task } }));
	};
	return (
		<>
			<EngagementsAccordion
				testid={`${task.subject}-card`}
				title={
					<Stack spacing={'0.25em'}>
						<Grid container spacing={'0.5em'}>
							<TaskIcon fontSize='small' />
							<span style={{ fontWeight: 'bold' }}>{task?.taskType}</span>
							<span>{' assigned to '}</span>
							<Stack direction='row'>
								{owner && (
									<BrokerAvatar
										broker={owner}
										width='1em'
										height='1em'
										sx={{ marginRight: '0.25em !important' }}
									/>
								)}
								{ownerName}
							</Stack>
							<span>{' | '}</span>
							<span style={{ fontWeight: '500' }}>
								Due {formatDate(dueDate, false)}
							</span>
						</Grid>
						{isClient && task?.dealId && (
							<DealInfo dealId={task.dealId} sx={{ paddingLeft: '2em' }} />
						)}
					</Stack>
				}
				expandedByDefault={openedTask}
				lastUpdated={lastUpdated}
				handleEdit={handleEditTask}
				loading={isLoading || isDeleteLoading}
				handleDelete={handleOpenDialog}
				contentPrefix={
					<TaskCheckbox task={task} isLoading={isLoading} completeTask={completeTask} />
				}
				content={
					<Typography
						variant='task_body'
						sx={{
							textDecoration: task.completed ? 'line-through' : 'none',
						}}
					>
						{task.subject}
					</Typography>
				}
				footerContent={
					<Stack alignItems={'center'} direction={'row'} spacing={1}>
						<Typography sx={minimalLabelTheme}>Priority</Typography>
						<Typography variant='subtitle2'>{task.priority}</Typography>
					</Stack>
				}
				expandedContent={
					<Typography
						variant='task_body'
						sx={{
							textDecoration: task.completed ? 'line-through' : 'none',
						}}
					>
						{parse(task.body ?? '')}
					</Typography>
				}
			/>
			<ConfirmDialog
				openDialog={dialogOpen}
				handleClose={handleCloseDialog}
				handleConfirm={handleConfirmDialog}
				actionName={'delete task'}
				additionalContent={
					'\nThis cannot be undone.\nPlease note that this will not delete any associated calendar invites.'
				}
			/>
		</>
	);
};
